import { LitElement, html, PropertyValues } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { RangeSliderEvents, RangeSliderTranslateStrings } from '../types';
import { WEB_COMPONENT_GZ_DROPDOWN_MENU, WEB_COMPONENT_GZ_SLIDER } from '../../../webcomponents.constants';
import { GzDropdownMenuWebComponent } from '../../gzDropdownMenu';
import { GzSliderWebComponent } from '../../gzSlider';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { SliderRange, SliderTranslateStrings } from '../../gzSlider/types';
import { ifDefined } from 'lit/directives/if-defined.js';
import { LabelWidthValues } from '../../../types/common/types';

export class GzRangeSliderInputWebComponent extends LitElement {
  // see https://lit-element.polymer-project.org/guide/properties
  @property({ type: String, attribute: true, reflect: true })
  message = '';

  @property({ type: String, reflect: true })
  label = '';

  @property({ type: String, reflect: true })
  placeholder = '';

  @property({ type: String, reflect: true })
  selectedValueString = '';

  @property({ type: String, reflect: true })
  autoAdjustMenuPosition = false;

  @property({ type: Object, reflect: true })
  translateStrings: RangeSliderTranslateStrings = {
    confirmButton: 'Confirm[**]',
    cancelButton: 'Cancel[**]',
    fromLabel: 'From[**]',
    toLabel: 'To[**]',
  };

  /**
   * Default set to 502, because that is the width of the slider element.
   */
  @property({ type: Number, reflect: true })
  offsetRight = 502;

  @property({ type: String, attribute: true, reflect: true })
  labelWidth = LabelWidthValues.MEDIUM;

  @state()
  private confirmBtnDisabled = true;

  /**
   * This property will store the min and max value from the slider
   */

  @property({ type: Object, reflect: true })
  private selectedSliderRange: SliderRange | undefined;

  @property({ type: Object, reflect: true })
  private activeSliderRange: SliderRange | undefined;

  @query(WEB_COMPONENT_GZ_DROPDOWN_MENU)
  private readonly dropdownMenu!: GzDropdownMenuWebComponent;

  @query(WEB_COMPONENT_GZ_SLIDER)
  private sliderComponent!: GzSliderWebComponent;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  updated(properties: PropertyValues): void {
    if (properties.has('selectedSliderRange')) {
      this.updateLabelWithSliderInterval(this.selectedSliderRange);
    }
  }

  render() {
    return html`
      <gz-dropdown-menu
        label="${this.label}"
        .labelWidth="${this.labelWidth}"
        placeholder="${this.placeholder}"
        selectedvaluesstrings="${this.selectedValueString}"
        ?autoAdjustMenuPosition="${this.autoAdjustMenuPosition}"
        .offsetRight="${this.offsetRight}"
        @open="${this.handleDropDownMenuOpen}"
      >
        <gz-slider
          .valueMax="${ifDefined(this.selectedSliderRange?.max)}"
          .valueMin="${ifDefined(this.selectedSliderRange?.min)}"
          .translateStrings=${{
            fromLabel: this.translateStrings.fromLabel,
            toLabel: this.translateStrings.toLabel,
          } as SliderTranslateStrings}
          @change="${this.changeSliderHandler}"
        ></gz-slider>
        <div class="buttons-container d-flex justify-end mt-1 mb-3 mr-2">
          <gz-button .disabled=${this.confirmBtnDisabled} @click="${this.confirmHandler}"
            >${this.translateStrings.confirmButton}</gz-button
          >
          <gz-button
            type="outline"
            color="secondary"
            buttonheight="medium"
            buttonfontsize="medium"
            class="mr-1 ml-2"
            @click="${this.closeDropDownMenu}"
            >${this.translateStrings.cancelButton}</gz-button
          >
        </div>
      </gz-dropdown-menu>
    `;
  }

  private handleDropDownMenuOpen(): void {
    if (this.selectedSliderRange?.min || this.selectedSliderRange?.max) {
      this.sliderComponent.valueMin = this.selectedSliderRange?.min;
      this.sliderComponent.valueMax = this.selectedSliderRange?.max;
    }
  }

  private changeSliderHandler(event: CustomEvent): void {
    this.activeSliderRange = {
      min: event.detail.min || 0,
      max: event.detail.max || 0,
    };
    this.confirmBtnDisabled = false;
  }

  private closeDropDownMenu(): void {
    this.dropdownMenu.open = false;
  }

  private confirmHandler(): void {
    this.selectedSliderRange = this.activeSliderRange;
    this.closeDropDownMenu();

    this.emitSliderChangeEvent(RangeSliderEvents.CHANGE_CONFIRMED);
  }

  public setSliderRange(selectedSliderRange: SliderRange | undefined): void {
    this.confirmBtnDisabled = true;
    this.selectedSliderRange = selectedSliderRange;
  }

  public clearSliderRange() {
    this.setSliderRange(undefined);
    this.sliderComponent.clearSlider();
  }

  private updateLabelWithSliderInterval(selectedSliderRange: SliderRange | undefined): void {
    if (!selectedSliderRange || isEmpty(selectedSliderRange)) {
      this.selectedValueString = '';
    } else {
      this.selectedValueString = selectedSliderRange.max + ' - ' + selectedSliderRange.min;
    }
  }

  protected emitSliderChangeEvent(eventName: string) {
    const event = new CustomEvent(eventName, {
      detail: {
        sourceEvent: this.selectedSliderRange,
      },
      bubbles: true,
      cancelable: true,
      composed: false,
    });
    this.dispatchEvent(event);
  }
}
